import axios from 'axios';
import { print } from 'graphql/language/printer';
import fileDownload from 'react-file-download';

import { AggrDiscountReport } from '@ce-openapi/reporting-service-ts-axios-client';
import axiosInstance from '../../utils/axiosInstance';
import * as api from '../constants';
import CategorySalesQuery from './gql/CategorySalesQuery.gql';
import DiscountQuery from './gql/DiscountQuery.gql';
import AggDiscountQuery from './gql/AggDiscountQuery.gql';
import GratuityQuery from './gql/GratuityQuery.gql';
import GrossProfitQuery from './gql/GrossProfitQuery.gql';
import ItemSalesQuery from './gql/ItemSalesQuery.gql';
import ItemBundleSalesQuery from './gql/ItemBundleSalesQuery.gql';
import SalesSummaryQuery from './gql/SalesSummaryQuery.gql';
import SalesTypeQuery from './gql/SalesTypeQuery.gql';
import ServedByQuery from './gql/ServedByQuery.gql';
import CollectedByQuery from './gql/CollectedByQuery.gql';
import TaxesQuery from './gql/TaxesQuery.gql';
import ModifierSalesQuery from './gql/ModifierSalesQuery.gql';
import PaymentMethodsQuery from './gql/PaymentMethodsQuery.gql';
import BrandSalesQuery from './gql/BrandSalesQuery.gql';
import {
  GET_TRANSACTION_SUMMARY_REPORT,
  GET_DAILY_TRANSACTION_SUMMARY_REPORT,
} from './queries';

function commonReject(error) {
  if (error.response) {
    const eobj = new Error(error.response.errors);
    return Promise.reject(eobj);
  }
  return Promise.reject(error);
}

/**
 *
 * @param {string} query
 * @param {Object} variables
 */
function queryReportingApi(query, variables) {
  return axiosInstance.post('/mkService/reporting-revamp/graphql', {
    query,
    variables,
  });
}

/**
 * @typedef {Object} SalesSummaryReport
 * @property {number} discount
 * @property {number} gratuity
 * @property {number} grossSales
 * @property {number} netSales
 * @property {number} redemption
 * @property {number} refund
 * @property {number} roundingAmount
 * @property {number} tax
 * @property {number} totalCollected
 *
 * @param {Object} data
 * @param {string} data.startDate
 * @param {string} data.endDate
 * @param {number[]} outletIds
 * @returns {Promise<SalesSummaryReport>}
 */
export function postSalesSummary(data, outletIds) {
  const { startDate, endDate } = data;
  return queryReportingApi(print(SalesSummaryQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    // Still need to do transformation
    const { salesSummaryReport } = response.data.data;
    return {
      ...salesSummaryReport,
    };
  });
}

export function getSalesType(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(SalesTypeQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    // Still need to do transformation
    const { salesTypeSummaryReport, salesTypeReport } = response.data.data;
    return {
      salesTypes: salesTypeReport.map((salesType) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        sales_type_name: salesType.salesTypeName,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        sales_type_count: salesType.numberOfTransactions,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        gross_sales: salesType.totalCollected,
      })),
      totalAmount: salesTypeSummaryReport.totalCollected,
    };
  });
}

export function postGrossProfit(params, outletIds) {
  const { startDate, endDate } = params;
  return queryReportingApi(print(GrossProfitQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { grossProfitReport } = response.data.data;
    return grossProfitReport;
  });
}

export function postPaymentMethods(data, outletIds) {
  return queryReportingApi(print(PaymentMethodsQuery), {
    startDate: data.startDate,
    endDate: data.endDate,
    outletIds,
  }).then((response) => {
    const { summary, paymentMethods } = response.data.data;

    const filteredMethods: any[] = [];

    for (const method of paymentMethods) {
      const { numberOfTransactions, items } = method;

      if (numberOfTransactions < 1) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const filteredItems = items.filter(
        (item) => item.numberOfTransactions > 0
      );

      if (filteredItems.length > 0) {
        filteredMethods.push({
          ...method,
          items: filteredItems,
        });
      }
    }

    return {
      ...summary,
      paymentMethods: filteredMethods,
    };
  });
}

/* Item Sales Optimize Version */
export function postItemSales(data, outletIds) {
  return queryReportingApi(print(ItemSalesQuery), {
    startDate: data.startDate,
    endDate: data.endDate,
    outletIds,
  }).then(({ data: d }) => d.data);
}

export function postBundleItemSales(data, outletIds) {
  return queryReportingApi(print(ItemBundleSalesQuery), {
    startDate: data.startDate,
    endDate: data.endDate,
    outletIds,
  }).then(({ data: d }) => d.data);
}

/* Category Sales Optimize Version */
export function postCategorySales(data, outletIds) {
  return queryReportingApi(print(CategorySalesQuery), {
    startDate: data.startDate,
    endDate: data.endDate,
    outletIds,
  }).then((response) => response.data.data);
}

export function postBrandSales(data, outletIds) {
  return queryReportingApi(print(BrandSalesQuery), {
    startDate: data.startDate,
    endDate: data.endDate,
    outletIds,
  }).then((response) => response.data.data);
}

/**
 * Get modifier sales.
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getModifierSales(params) {
  const { startDate, endDate, outletIds } = params;

  const promise = queryReportingApi(print(ModifierSalesQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { modifierSales } = response.data.data;
    return modifierSales;
  });

  // TODO There is no way to get the total values yet. So we have to compute
  // those for both. We need to update this once API has them.
  return promise
    .then((modifierSales) => {
      let totalQuantity = 0;
      let totalGrossSales = 0;
      let totalDiscount = 0;
      let totalRefund = 0;
      let totalNetSales = 0;
      for (const entry of modifierSales) {
        const { quantitySold, grossSales, discount, refund, netSales } = entry;
        totalQuantity += quantitySold;
        totalGrossSales += Math.round(grossSales);
        totalDiscount += Math.round(discount);
        totalRefund += Math.round(refund);
        totalNetSales += Math.round(netSales);
      }

      return {
        modifierSales,
        totalQuantity,
        totalGrossSales,
        totalDiscount,
        totalRefund,
        totalNetSales,
      };
    })
    .catch((error) => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * Get discounts.
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getDiscounts(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(DiscountQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { discountReport } = response.data.data;
    return discountReport.map((discount) => ({
      amount: discount.discountAmount,
      count: discount.count,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      discount_applied: discount.grossDiscount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      discount_type: discount.discountType,
      name: discount.name,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      net_discount: discount.netDiscount,
      refund: discount.discountRefunded,
    }));
  });
}

export function getCustomDiscounts(params): Promise<AggrDiscountReport[]> {
  return queryReportingApi(print(AggDiscountQuery), params).then(
    (response) => response.data.data.aggrDiscountReport
  );
}

/**
 * Get taxes.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getTaxes(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(TaxesQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { taxesReport, taxesSummaryReport } = response.data.data;
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      reports_taxes: taxesReport.map((tax) => ({
        amount: tax.taxRate,
        name: tax.taxName,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        tax_collected: tax.taxCollected,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        taxable_amount: tax.taxableAmount,
      })),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_tax_collected: taxesSummaryReport.taxCollected,
    };
  });
}

/**
 * Get Gratuities.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getGratuities(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(GratuityQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { gratuityReport, gratuitySummaryReport } = response.data.data;
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      reports_gratuities: gratuityReport.map((gratuity) => ({
        amount: gratuity.gratuityRate,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        gratuity_collected: gratuity.gratuityCollected,
        name: gratuity.gratuityName,
      })),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_gratuity_collected: gratuitySummaryReport.gratuityCollected,
    };
  });
}

/**
 * Get Collected By data (staff).
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getStaffs(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(CollectedByQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { collectedByReport, collectedBySummaryReport } = response.data.data;
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      staff_reports: collectedByReport.map((collector) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_id: collector.collectorId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_name: collector.collectorName,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_title: collector.collectorTitle,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_transaction_amount: collector.totalCollected,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_transaction_count: collector.numberOfTransactions,
      })),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transaction_amount: collectedBySummaryReport.totalCollected,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transaction_count: collectedBySummaryReport.numberOfTransactions,
    };
  });
}

/**
 * Get servers.
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 * @returns {Promise.<T>|*|Promise<R>} JSON object from server, or Error object if fails.
 */
export function getServers(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(print(ServedByQuery), {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    const { servedByReport, servedBySummaryReport } = response.data.data;
    return {
      servers: servedByReport.map((report) => ({
        collected: report.totalCollected,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        number_transaction: report.numberOfTransactions,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        server_id: report.serverId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        server_name: report.serverName,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        server_title: report.serverTitle,
      })),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_collected: servedBySummaryReport.totalCollected,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_number_transaction: servedBySummaryReport.numberOfTransactions,
    };
  });
}

export function exportReport(params) {
  return axiosInstance.get('/mkService/reporting-revamp/export', {
    responseType: 'blob',
    params,
  });
}

export function exportTransaction(query) {
  return axios
    .get(`${api.EXPORT_TRANSACTION}`, { params: query, withCredentials: true })
    .catch(commonReject);
}

export function exportSummary(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const reportType =
    outletIds.length > 1 ? 'salesSummaryPerOutletReport' : 'salesSummaryReport';
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'sales',
    reportType,
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportSalesType(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportCategory: 'sales',
    reportType: 'salesTypeReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function exportGrossProfit(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const reportType =
    outletIds.length > 1 ? 'grossProfitPerOutletReport' : 'grossProfitReport';
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'sales',
    reportType,
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportHourlyItemSold(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'items',
    reportType: 'itemSoldHourlyReport',
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportHourlyItemAmount(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'items',
    reportType: 'amountSoldHourlyReport',
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportPaymentMethod(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const reportType =
    outletIds.length > 1
      ? 'paymentMethodPerOutletReport'
      : 'paymentMethodReport';
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'sales',
    reportType,
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportItemSales(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'items',
    reportType: 'itemSalesReport',
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportCategorySales(query, filename, outletIds) {
  const { startDate, endDate } = query;
  const extension = 'xlsx';

  const promise = exportReport({
    startDate,
    endDate,
    outletIds: outletIds.join(','),
    reportCategory: 'items',
    reportType: 'categorySalesReport',
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportModifierSales(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'modifierSalesReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function exportDiscounts(query) {
  const { start, end, outletIds, filename } = query;
  const extension = 'xlsx';
  const promise = exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'discountReport',
  });

  return promise
    .then((response) => {
      fileDownload(response.data, `${filename}.${extension}`);
    })
    .catch(commonReject);
}

export function exportTaxes(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'taxesReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function exportGratuity(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'gratuityReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function exportColectedBy(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'collectedByReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function exportServedBy(query) {
  const { start, end, outletIds, filename } = query;
  return exportReport({
    startDate: start,
    endDate: end,
    outletIds: outletIds.join(','),
    reportType: 'servedByReport',
  }).then((response) => {
    fileDownload(response.data, `${filename}.xlsx`);
  });
}

export function getOrderSummary(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(GET_TRANSACTION_SUMMARY_REPORT, {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    return response.data.data.transactionSummaryReport;
  });
}

export function getDailyOrderSummaries(params) {
  const { startDate, endDate, outletIds } = params;
  return queryReportingApi(GET_DAILY_TRANSACTION_SUMMARY_REPORT, {
    startDate,
    endDate,
    outletIds,
  }).then((response) => {
    return response.data.data.dailyTransactionSummaryReport;
  });
}
